import { createContext, useContext, useState, ReactNode } from "react";
import { Customer } from "app/models/customer";
import storage from "app/lib/storage";
import useUpdatedEffect from "app/hooks/useUpdatedEffect";
import { ACTIVE_CUSTOMER_KEY } from "app/constants/variables";
import { useActiveProfile } from "app/providers/ActiveProfileProvider";
const ActiveCustomerContext = createContext<Customer | null>(null);
ActiveCustomerContext.displayName = "ActiveCustomerContext";
const SetActiveCustomerContext =
  createContext<((customer: Customer | null) => void) | undefined>(undefined);
SetActiveCustomerContext.displayName = "SetActiveCustomerContext";

interface ActiveCustomerAccountProviderProps {
  children: ReactNode;
}

const ActiveCustomerAccountProvider = ({
  children,
}: ActiveCustomerAccountProviderProps) => {
  const [activeCustomer, setActiveCustomer] = useState<Customer | null>(() => {
    return storage.get(ACTIVE_CUSTOMER_KEY);
  });

  useUpdatedEffect(() => {
    if (activeCustomer) {
      storage.set(ACTIVE_CUSTOMER_KEY, activeCustomer);
    } else {
      storage.remove(ACTIVE_CUSTOMER_KEY);
    }
  }, [activeCustomer]);

  return (
    <ActiveCustomerContext.Provider value={activeCustomer}>
      <SetActiveCustomerContext.Provider value={setActiveCustomer}>
        {children}
      </SetActiveCustomerContext.Provider>
    </ActiveCustomerContext.Provider>
  );
};

export default ActiveCustomerAccountProvider;

export const useActiveCustomer = () => {
  const context = useContext(ActiveCustomerContext);
  if (context === undefined) {
    throw new Error(
      "useActiveCustomer must be used within an ActiveCustomerAccountProvider"
    );
  }
  return context;
};

export const useSetActiveCustomer = () => {
  const context = useContext(SetActiveCustomerContext);
  if (!context) {
    throw new Error(
      "useSetActiveCustomer must be used within an ActiveCustomerAccountProvider"
    );
  }
  return context;
};

/**
 * Hook to get the active customer account that belongs to the current active profile
 * @returns The active customer account if it belongs to the current active profile, null otherwise
 */
export const useActiveCustomerAccount = () => {
  const activeCustomer = useActiveCustomer();
  const activeProfile = useActiveProfile();

  return activeCustomer && activeCustomer.business_id === activeProfile?.id
    ? activeCustomer
    : null;
};

/**
 * Hook to determine if the current view is in customer mode
 * @returns {boolean} True if there is an active customer account and the current profile is a business profile
 */
export const useIsCustomerMode = () => {
  const activeCustomer = useActiveCustomerAccount();
  const activeProfile = useActiveProfile();

  return Boolean(activeCustomer && activeProfile?.profile_type === "business");
};

/**
 * Hook to determine if the current view is in business customer mode
 * @returns {boolean} True if there is an active customer account and the customer is a business customer
 */
export const useIsBusinessCustomerMode = (): boolean => {
  const activeCustomer = useActiveCustomerAccount();

  return Boolean(activeCustomer && activeCustomer.type === "business");
};